import { useContext, useMemo } from "react";
import styles from "./styles.module.css";
import CloseIcon from "@mui/icons-material/Close";
import ButtonGray from "components/utils/CustomFields/ButtonGray";
import ShopContext from "context/ShopContext";
import { amountRoundOff } from "helpers/NumberFormatHelper";
import ChairIcon from "@mui/icons-material/Chair";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import ButtonRed from "components/utils/CustomFields/ButtonRed";
import { TABLE_STATUS, SALE_STATUS } from "const";

export default function TableChairDialog({
  open,
  onClose,
  table,
  activeSales = [],
  isSelectionMode = false,
  onSelectTable,
  onSelectChair,
  onSelectSale,
  onViewOrders,
  onViewAllOrders,
}) {
  const { selectedShop } = useContext(ShopContext);

  const tableLevelSales = useMemo(() => {
    return (activeSales || []).filter((s) => !s.table?.chair);
  }, [activeSales]);

  const tableLevelTotal = useMemo(() => {
    return tableLevelSales.reduce((sum, s) => sum + (s.payable || 0), 0);
  }, [tableLevelSales]);

  const deriveStatus = (sales) => {
    if (!sales || sales.length === 0) return TABLE_STATUS.AVAILABLE;
    const hasPrinted = sales.some((s) => (s.status || "").toLowerCase() === SALE_STATUS.PRINTED);
    if (hasPrinted) return TABLE_STATUS.NOT_PAID;
    return TABLE_STATUS.DINE_IN;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case TABLE_STATUS.AVAILABLE:
        return styles.available;
      case TABLE_STATUS.DINE_IN:
        return styles.ongoing;
      case TABLE_STATUS.NOT_PAID:
        return styles.notPaid;
      default:
        return styles.available;
    }
  };

  const getOccupiedStatusLabel = (status) => {
    return status === TABLE_STATUS.NOT_PAID ? "NOT PAID" : "OCCUPIED";
  };

  const tableLevelStatus = useMemo(() => {
    return deriveStatus(tableLevelSales);
  }, [tableLevelSales]);

  const chairs = table?.chairs || [];

  const gridColumnsStyle = useMemo(() => {
    const count = chairs.length;
    if (count <= 0) return {};
    if (count >= 3 && count <= 6) {
      return {
        gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      };
    }
    if (count < 3) {
      return {
        gridTemplateColumns: `repeat(auto-fit, minmax(160px, 1fr))`,
        maxWidth: `${count * 240}px`,
      };
    }
    return {
      gridTemplateColumns: `repeat(auto-fill, minmax(130px, 1fr))`,
    };
  }, [chairs.length]);

  if (!open || !table) return null;

  const getChairSales = (chairId) => {
    return (activeSales || []).filter((s) => Number(s.table?.chair?.id) === Number(chairId));
  };

  const handleTableClick = () => {
    if (isSelectionMode) {
      onSelectTable?.(table);
      return;
    }
    if (tableLevelSales.length === 1 && onSelectSale) {
      onSelectSale(tableLevelSales[0]);
    } else if (tableLevelSales.length > 1 && onViewOrders) {
      onViewOrders(tableLevelSales, `${table.name} (Entire Table) - Orders`);
    } else {
      onSelectTable?.(table);
    }
  };

  const handleChairClick = (chair) => {
    if (isSelectionMode) {
      onSelectChair?.(chair);
      return;
    }
    const chairSales = getChairSales(chair.id);
    if (chairSales.length === 1 && onSelectSale) {
      onSelectSale(chairSales[0]);
    } else if (chairSales.length > 1 && onViewOrders) {
      onViewOrders(chairSales, `${table.name} - ${chair.name || `Chair ${chair.id}`} - Orders`);
    } else {
      onSelectChair?.(chair);
    }
  };

  const handleNewOrderOnChair = (e, chair) => {
    e.stopPropagation();
    onSelectChair?.(chair);
  };

  const handleNewOrderOnTable = (e) => {
    e.stopPropagation();
    onSelectTable?.(table);
  };

  return (
    <div className={styles.popUp} onClick={onClose}>
      <div className={styles.contentWrapper} onClick={(e) => e.stopPropagation()}>
        {/* Header Section */}
        <div className={styles.headInfoSec}>
          <div className={styles.headerTitleWrapper}>
            <h2>{table.name}</h2>
            {table.area?.name && <span className={styles.areaBadge}>{table.area.name}</span>}
          </div>
          <CloseIcon className={styles.icon} onClick={onClose} />
        </div>

        {/* Content Section */}
        <div className={styles.contentArea}>
          {/* Entire Table Section */}
          <div>
            <div className={styles.sectionTitle}>Table Section</div>
            <div
              className={`${styles.tableSectionCard} ${getStatusClass(tableLevelStatus)}`}
              onClick={handleTableClick}
            >
              <div className={styles.tableLeft}>
                <div className={styles.tableIconBox}>
                  <TableRestaurantIcon fontSize="medium" />
                </div>
                <div>
                  <h4 className={styles.tableName}>{table.name} (Entire Table)</h4>
                  <p className={styles.tableSubtext}>
                    {tableLevelSales.length > 1
                      ? `${getOccupiedStatusLabel(tableLevelStatus)} - ${tableLevelSales.length} Orders`
                      : tableLevelSales.length === 1
                        ? `${getOccupiedStatusLabel(tableLevelStatus)} - Order #${tableLevelSales[0].local_id}`
                        : "Attach order to the entire table without selecting a chair"}
                  </p>
                </div>
              </div>
              <div className={styles.tableRight}>
                {tableLevelSales.length > 0 ? (
                  <>
                    <span className={styles.chairAmount}>
                      {selectedShop?.currency_symbol} {amountRoundOff(tableLevelTotal)}
                    </span>
                    <span
                      className={`${styles.statusPill} ${
                        tableLevelStatus === TABLE_STATUS.NOT_PAID ? styles.statusNotPaid : styles.statusOccupied
                      }`}
                    >
                      {tableLevelStatus === TABLE_STATUS.NOT_PAID
                        ? getOccupiedStatusLabel(tableLevelStatus)
                        : tableLevelSales.length > 1
                          ? `${tableLevelSales.length} Orders`
                          : getOccupiedStatusLabel(tableLevelStatus)}
                    </span>

                    {!isSelectionMode && <ButtonRed onClick={handleNewOrderOnTable}>+ New Order</ButtonRed>}
                  </>
                ) : (
                  <span className={`${styles.statusPill} ${styles.statusAvailable}`}>Available</span>
                )}
              </div>
            </div>
          </div>

          {/* Divider */}
          {chairs.length > 0 && <div className={styles.divider}>OR SELECT A CHAIR</div>}

          {/* Chairs Section */}
          {chairs.length > 0 && (
            <div>
              <div className={styles.sectionTitle}>Chairs ({chairs.length})</div>
              <div className={styles.chairsGrid} style={gridColumnsStyle}>
                {chairs.map((chair) => {
                  const chairSales = getChairSales(chair.id);
                  const status = deriveStatus(chairSales);
                  const isOccupied = status !== TABLE_STATUS.AVAILABLE;
                  const chairTotal = chairSales.reduce((sum, s) => sum + (s.payable || 0), 0);

                  return (
                    <div
                      key={chair.id}
                      className={`${styles.chairCard} ${getStatusClass(status)}`}
                      onClick={() => handleChairClick(chair)}
                    >
                      <div className={styles.chairIconBox}>
                        <ChairIcon fontSize="small" />
                      </div>
                      <div className={styles.chairName}>{chair.name || `Chair ${chair.id}`}</div>

                      {isOccupied ? (
                        <>
                          <span
                            className={
                              status === TABLE_STATUS.NOT_PAID ? styles.chairStatusNotPaid : styles.chairStatusOccupied
                            }
                          >
                            {getOccupiedStatusLabel(status)}
                          </span>
                          <div className={styles.chairOrderInfo}>
                            <span>
                              {chairSales.length > 1
                                ? `${chairSales.length} Orders`
                                : `Order #${chairSales[0].local_id}`}
                            </span>
                            <span className={styles.chairAmount}>
                              {selectedShop?.currency_symbol} {amountRoundOff(chairTotal)}
                            </span>
                          </div>
                          {!isSelectionMode && (
                            <div className={styles.chairActions}>
                              <ButtonRed onClick={(e) => handleNewOrderOnChair(e, chair)}>New Order</ButtonRed>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className={styles.chairStatus}>AVAILABLE</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer Section */}
        <div className={styles.buttonSection}>
          {activeSales.length > 1 && onViewAllOrders ? (
            <ButtonGray onClick={onViewAllOrders}>View All Orders ({activeSales.length})</ButtonGray>
          ) : (
            <div />
          )}
          <div className={styles.buttonGroupRight}>
            <ButtonGray onClick={onClose}>Cancel</ButtonGray>
          </div>
        </div>
      </div>
    </div>
  );
}
